<script setup lang="ts">
import type { RouteLocationNamedRaw, RouteLocationRaw } from 'vue-router';
import { computed, toRef } from 'vue';

import { transformNumber } from '@/utils/numbers';
import VButton from '@components/buttons/VButton/VButton.vue';
import VIconButton from '@components/buttons/VIconButton/VIconButton.vue';
import VLogo from '@components/logo/VLogo/VLogo.vue';
import VMessage from '@components/message/VMessage/VMessage.vue';
import { AFF_BALANCES_PERMISSIONS, AFFILIATE_BALANCES_LIST_ROUTE_NAME } from '@modules/aff-balances/constants';
import { useAffiliateBalanceQuery } from '@modules/aff-balances/queries';
import { useConfig } from '@modules/config/composables';
import { useDevice } from '@modules/core/composables/device';
import { ERouteNamespace } from '@modules/core/services/router/enums';
import setHeaderHeight from '@modules/core/utils/setHeaderHight';
import TheGlobalLoader from '@modules/header/components/TheGlobalLoader/TheGlobalLoader.vue';
import TheHeaderBottomMenu from '@modules/header/components/TheHeaderMobileMenu/TheHeaderMobileMenu.vue';
import TheLanguageSelector from '@modules/i18n/components/TheLanguageSelector/TheLanguageSelector.vue';
import TheMerchantSelector from '@modules/merchant/components/TheMerchantSelector/TheMerchantSelector.vue';
import { useAddMerchantIdToRoute } from '@modules/merchant/composables';
import HeaderNotification from '@modules/notification/components/HeaderNotification/HeaderNotification.vue';
import { useCurrentNamespace, useNamespaceRoutes } from '@modules/permissions/composables';
import { usePermissionsStore } from '@modules/permissions/store';
import { PROFILE_SETTINGS_PERMISSIONS } from '@modules/profile-settings/constants';
import {
  ADMIN_PROFILE_SETTINGS_GENERAL_SETTINGS_ROUTE_NAME,
  AFFILIATE_PROFILE_SETTINGS_GENERAL_SETTINGS_ROUTE_NAME,
} from '@modules/profile-settings/submodules/general-settings/constants';
import { useSidebarStore } from '@modules/sidebar/store';
import { useUserSupervision } from '@modules/user/composables';
import { useUserStore } from '@modules/user/store';
import { watchImmediate } from '@vueuse/core';

import { TRANSLATION_GROUP_HEADER, TRANSLATION_SUPERVISION_NOTIFICATION_GROUP_HEADER } from '../../constants';

const user = toRef(useUserStore(), 'user');
const sidebarStore = useSidebarStore();
const { isDesktop } = useDevice();
const { isSupervision } = useUserSupervision();

const { config } = useConfig();
const { hasPermission } = usePermissionsStore();
const { data: balance, isLoading: isBalanceLoading } = useAffiliateBalanceQuery();

const { isAdminNamespace, isAffiliateNamespace } = useCurrentNamespace();
const { isAffiliateRoute } = useNamespaceRoutes();
const { signOut } = useUserStore();
const { addMerchantId } = useAddMerchantIdToRoute();

const userBalance = computed(() => {
  if (!isBalanceLoading.value && balance.value !== undefined && hasPermission(AFF_BALANCES_PERMISSIONS.SHOW_BALANCE).value) {
    return `${config.value?.currency} ${transformNumber(balance.value || '')}`;
  }
  return undefined;
});

const balanceRoute = computed<RouteLocationRaw>(() => addMerchantId({
  name: AFFILIATE_BALANCES_LIST_ROUTE_NAME,
}));

const settingsRoute = computed<RouteLocationNamedRaw>(() => ({
  name: isAdminNamespace.value ? ADMIN_PROFILE_SETTINGS_GENERAL_SETTINGS_ROUTE_NAME : AFFILIATE_PROFILE_SETTINGS_GENERAL_SETTINGS_ROUTE_NAME,
}));

watchImmediate(isSupervision, setHeaderHeight);
</script>

<template>
  <div v-data-qa-auto-id="'TheHeader'" class="z-20 h-header-height w-full">
    <VMessage
      v-if="isSupervision"
      class="h-supervision-notification-height w-full text-center"
      :pt="{
        content: { class: 'justify-center' },
      }"
      severity="warn"
    >
      <div class="flex items-center gap-2">
        <div class="line-clamp-1">
          {{ $getTranslation(TRANSLATION_SUPERVISION_NOTIFICATION_GROUP_HEADER, 'ADMIN_CANNOT_MODIFY_ANY_DATA', { email: user?.email }) }}
        </div>
        <VButton
          :label="$getTranslation(TRANSLATION_SUPERVISION_NOTIFICATION_GROUP_HEADER, 'STOP_SUPERVISION')"
          size="small"
          @click="signOut"
        />
      </div>
    </VMessage>
    <header
      v-data-test="{ el: 'header' }"
      class="relative z-20 flex h-header-default-height w-full items-center justify-between bg-header-color px-4 text-content-color"
    >
      <div class="flex w-2/5 justify-start">
        <TheGlobalLoader />
        <TheMerchantSelector
          v-if="isDesktop"
          class="m-0"
        />
        <VIconButton
          v-else
          icon="pi pi-bars"
          text-color="white"
          @click="sidebarStore.toggle"
        />
      </div>
      <div class="flex h-full items-center justify-center p-2">
        <div class="h-full max-h-[300px]">
          <VLogo type="logo_mini" />
        </div>
      </div>
      <div class="flex w-2/5 justify-end">
        <div
          v-if="isDesktop"
          class="flex items-center justify-end gap-2"
        >
          <div
            v-if="userBalance"
            class="mr-4 flex items-center gap-2"
          >
            <span class="text-primary-600">
              {{ $getTranslation(TRANSLATION_GROUP_HEADER, 'BALANCE_IN_HEADER') }}
            </span>
            <RouterLink
              class="whitespace-nowrap font-medium"
              :to="balanceRoute"
            >
              {{ userBalance }}
            </RouterLink>
          </div>
          <p class="mr-3 whitespace-nowrap font-bold">
            {{ user?.email }}
          </p>
          <TheLanguageSelector class="m-0" />
          <HeaderNotification
            v-if="isAffiliateNamespace && isAffiliateRoute"
            :namespace="ERouteNamespace.AFFILIATE"
          />
          <VIconButton
            v-if="$hasPermission(PROFILE_SETTINGS_PERMISSIONS.SHOW)"
            icon="pi-cog"
            text-color="white"
            :to="settingsRoute"
          />
          <VIconButton
            icon="pi-sign-out"
            text-color="white"
            @click="signOut"
          />
        </div>
        <div
          v-else
          class="flex gap-2"
        >
          <HeaderNotification
            v-if="isAffiliateNamespace"
            :namespace="ERouteNamespace.AFFILIATE"
          />
          <TheHeaderBottomMenu
            :user-email="user?.email"
            :user-balance="userBalance"
            :settings-route="settingsRoute"
            :balance-route="balanceRoute"
            @sign-out="signOut"
          />
        </div>
      </div>
    </header>
  </div>
</template>
